import type { Lang } from "./commentary.js";

/**
 * Situational commentary.
 *
 * Ball-by-ball describes what just happened. What makes a broadcast sound like
 * a broadcast is the other half — the talk *between* the balls, about where the
 * match actually is. A commentator who only ever described deliveries would
 * sound like a scorecard being read aloud.
 *
 * So this reads the match state and says something about it: the field is up in
 * the powerplay, the required rate has crept past ten, these two have put on
 * fifty, nobody has hit a boundary in four overs, it is the last over and they
 * need six.
 *
 * It fires sparingly. A line every ball would be worse than none — the point is
 * that it arrives when there is genuinely something to say.
 */

export interface Situation {
  /** Which phase of the innings. */
  phase: "opening" | "middle" | "death";
  over: number;
  totalOvers: number;
  runs: number;
  wickets: number;
  maxWickets: number;
  runRate: number;
  /** Chasing only. */
  need: number | null;
  ballsLeft: number | null;
  required: number | null;
  /** Balls since the last boundary, and since the last wicket. */
  sinceBoundary: number;
  sinceWicket: number;
  /** Runs in the current partnership, and how long it has lasted. */
  partnership: number;
  partnershipBalls: number;
  /** Consecutive dot balls immediately before now. */
  dots: number;
  battingTeam: string;
  bowlingTeam: string;
}

/* ── the banks ──────────────────────────────────────────────────────────── */

const EN: Record<string, string[]> = {
  opening: [
    "Early days here, and the field is up. Anything in the gap runs away.",
    "The new batters will want to see off a few before they start playing shots.",
    "First few overs, and this is about survival as much as scoring.",
    "The field is inside the circle, and both sides know these overs matter.",
    "A watchful start, and there is no need to force anything yet.",
    "The bowlers have the ball hard and new. This is their best chance.",
    "Both openers still finding the pace of this pitch.",
    "A cagey start, and neither side has landed a blow yet.",
    "The ball is coming on nicely, and there are runs here for anyone who is patient.",
  ],
  middle: [
    "Into the middle overs, and this is where an innings is built or lost.",
    "The field has spread now, and the singles are there for the taking.",
    "This is the consolidation phase. Rotate the strike, keep wickets in hand.",
    "Neither side has taken control of this passage yet.",
    "The spinners are on, and the run rate has settled.",
    "A period of quiet accumulation, and that is exactly what is needed.",
    "The game has gone quiet, and that usually favours the fielding side.",
    "Singles are being turned down, and that is a sign of nerves.",
    "A holding pattern, and someone will have to break it soon.",
    "The captain is rotating his bowlers, looking for the right match-up.",
    "Runs are coming, but so is the risk. A fine balance out there.",
  ],
  death: [
    "Into the closing overs, and every single delivery matters now.",
    "The death overs. This is where matches are won and lost.",
    "Fielders back on the rope, and the batters will have to find the gaps.",
    "No more building. From here it is about maximum runs.",
    "The final stretch, and nerves will play their part.",
    "Yorkers and slower balls from here. Anything full and straight goes.",
    "The batters are looking for the boundary every ball now.",
    "One good over from either side settles this.",
    "Field spread to the rope, and the singles are on offer if they want them.",
  ],
  lastOver: [
    "Final over. Everything comes down to these six balls.",
    "Last over of the innings, and the crowd is on its feet.",
    "Here we go then. Six balls left, and the match hangs on them.",
    "The last over, and you can hear a pin drop out there.",
    "Six balls to decide it. This is what everyone came for.",
  ],
  chaseTight: [
    "This is beautifully poised. Neither side has taken hold of it.",
    "The equation is tightening with every delivery now.",
    "You would not want to look away. This could go either way.",
    "Nerves out in the middle, and nerves in the stands.",
    "The required rate and the wickets in hand are pulling against each other.",
    "Every run matters now, and every dot ball hurts.",
    "The match is on a knife edge, and one shot could settle it.",
    "Both captains are out of easy options. Instinct from here.",
    "One over could win it or lose it. That is where we are.",
  ],
  chaseComfortable: [
    "They are strolling towards this. Barely a moment of trouble.",
    "The chase is under control, and the batters know it.",
    "Comfortable from here, barring something remarkable.",
    "This looks all but done. The rate is well within reach.",
    "Very little drama left in this one now.",
    "They have taken the risk out of it, and it shows.",
    "Wickets in hand and runs on the board. A textbook chase.",
  ],
  chaseSlipping: [
    "The required rate is climbing, and that is the pressure telling.",
    "This is drifting away. They need something to change, and quickly.",
    "Dot balls are the enemy now, and there have been too many.",
    "The asking rate is beyond the run rate, and the gap is widening.",
    "The boundaries have dried up at exactly the wrong moment.",
    "They need a big over, and they need it now.",
    "The maths is getting harder with every delivery.",
    "One more quiet over and this is beyond them.",
  ],
  chaseAlmost: [
    "Almost there now. A handful of runs between them and the win.",
    "The finish line is in sight, and the batters can smell it.",
    "A single would do it. The tension is extraordinary.",
    "Nearly home, and the dressing room is on its feet.",
  ],
  boundaryDrought: [
    "No boundary for a while now, and the pressure is quietly building.",
    "The rope has not been found in some time. The field is doing its job.",
    "Singles and twos only for several overs. That suits the bowling side.",
    "The bowlers have found their length, and the runs have stopped.",
    "Nothing but singles for a long while now. Something has to give.",
    "The batters are getting frustrated, and that is when wickets fall.",
  ],
  boundaryFlurry: [
    "The boundaries are flowing now, and the momentum has shifted.",
    "Three boundaries in quick succession, and the crowd has come alive.",
    "This has turned into a proper assault. The bowlers need an answer.",
    "The game has broken open in the space of a few balls.",
    "The bowler cannot find a length, and he is being punished for it.",
    "Boundaries are coming freely, and the field looks helpless.",
  ],
  partnership50: [
    "That is a fifty partnership, and it has rescued this innings.",
    "Fifty for the stand, and these two look increasingly settled.",
    "A half-century partnership, built carefully and without fuss.",
    "Fifty for the partnership, and both have contributed handsomely.",
    "A crucial fifty stand. This is the passage that changed the innings.",
  ],
  partnershipBuilding: [
    "A useful stand developing here, and the innings has steadied.",
    "These two are running well and finding the gaps at will.",
    "The partnership is growing, and with it the confidence.",
    "They have weathered the worst of it and are starting to look comfortable.",
    "Sensible cricket, and the scoreboard is ticking without risk.",
    "A stand worth its weight. This is how you rebuild.",
  ],
  wicketsInHand: [
    "Plenty of wickets in hand, and that gives them licence to attack.",
    "Batting deep here, so they can afford to take a risk or two.",
    "They can afford to lose one here, and that changes how they will play.",
    "Wickets in the bank, and the licence to use them.",
  ],
  tailEnd: [
    "Into the lower order now, and the innings could fold quickly.",
    "The recognised batting is gone. Every ball is a danger from here.",
    "Not much left in the shed. The bowlers will fancy their chances.",
    "The batting is thin from here. One more and the innings folds.",
    "The tail is exposed, and the fielding side can smell it.",
  ],
  collapse: [
    "The wheels have come off. Wicket after wicket, and no partnership.",
    "This has become a procession. Nobody has been able to settle.",
    "A dramatic collapse, and the innings is in genuine trouble.",
    "Three down in quick succession, and the innings is unravelling.",
    "Nothing is going right. Every plan has failed in the last few overs.",
  ],
  newBatterSettling: [
    "The new batter is still finding his feet out there.",
    "Fresh at the crease, and the bowlers will test him early.",
    "A few balls to settle, and then he can start to look for runs.",
    "A tricky time to walk in, with the bowler on top.",
    "He will want a single early to get himself going.",
  ],
  bowlerOnTop: [
    "The bowler is on top here, and the batters have no answer.",
    "A superb spell, and the pressure is entirely one way.",
    "This has been a wonderful spell, and it deserves a wicket.",
    "Line, length, and no room. The batters cannot breathe.",
  ],
  hatTrickBall: [
    "Two in two! And now the hat-trick ball. The field crowds in.",
    "On a hat-trick, and every fielder is up. This is the moment.",
  ],
  goodOverStart: [
    "New bowler into the attack, and the captain wants a wicket.",
    "A change of bowling, and a change of angle with it.",
    "A fresh bowler, a fresh angle, and a chance to change the game.",
    "The captain turns to his change bowler at an important moment.",
  ],
};

const HI: Record<string, string[]> = {
  opening: [
    "Shuruaati over hain, aur field andar hai. Gap mein kuch bhi gaya toh chaar.",
    "Naye batsman thoda dekh kar khelenge, jaldi shot nahi lagayenge.",
    "Pehle kuch over, aur yahan bachna zaroori hai, run banana baad mein.",
    "Gend nayi aur sakht hai. Gendbaaz ke liye yahi sabse achha mauka hai.",
    "Dono openers abhi pitch ka andaza laga rahe hain.",
    "Sambhal kar shuruat, kisi ne abhi tak hamla nahi kiya.",
  ],
  middle: [
    "Middle overs mein aa gaye, aur yahin paari banti ya bigadti hai.",
    "Field faila diya gaya hai, ab single aasani se mil rahe hain.",
    "Yeh consolidation ka daur hai. Strike rotate karo, wicket bachao.",
    "Spinners aa gaye hain, aur run rate thoda thehar gaya hai.",
    "Khel thoda thehar gaya hai, aur yeh fielding side ke haq mein hai.",
    "Kaptaan gendbaaz badal raha hai, sahi jodi dhoondh raha hai.",
    "Run aa rahe hain, lekin khatra bhi. Nazuk santulan hai.",
  ],
  death: [
    "Aakhiri over shuru ho gaye, ab har gend keemti hai.",
    "Death overs. Yahin match jeeta aur haara jaata hai.",
    "Fielders boundary par hain, ab gap dhoondhna hoga.",
    "Ab building nahi, ab sirf maximum runs.",
    "Ab yorker aur slower ball. Kuch bhi full aur seedha gaya toh chaar.",
    "Batsman har gend par boundary dhoondh rahe hain.",
    "Ek achha over kisi bhi taraf se faisla kar dega.",
  ],
  lastOver: [
    "Aakhiri over. Sab kuch in chhe gendon par tika hai.",
    "Paari ka aakhiri over, aur crowd khada ho gaya hai.",
    "Aakhiri over, aur maidan mein sannata hai.",
  ],
  chaseTight: [
    "Mukabla bilkul barabar hai. Kuch bhi ho sakta hai.",
    "Har gend ke saath samikaran kasta ja raha hai.",
    "Nazar hatana mushkil hai. Dono taraf jhukav ho sakta hai.",
    "Ab har run keemti hai, aur har dot bhaari.",
    "Match chaku ki dhaar par hai. Ek shot faisla kar sakta hai.",
  ],
  chaseComfortable: [
    "Aaram se lakshya ki taraf badh rahe hain. Koi pareshani nahi.",
    "Chase control mein hai, aur batsman ko yeh pata hai.",
    "Ab is match mein zyada drama nahi bacha.",
    "Wicket haath mein aur run board par. Kitaabi chase.",
  ],
  chaseSlipping: [
    "Required rate badhta ja raha hai, aur dabaav saaf dikh raha hai.",
    "Match haath se nikal raha hai. Kuch bada karna hoga, jaldi.",
    "Dot balls dushman ban gayi hain, aur bahut zyada ho gayi hain.",
    "Boundary ruk gayi hain, aur bilkul galat waqt par.",
    "Ek bada over chahiye, aur abhi chahiye.",
    "Ganit har gend ke saath mushkil hota ja raha hai.",
  ],
  chaseAlmost: [
    "Bas kuch hi run baaki hain. Jeet saamne dikh rahi hai.",
    "Ek run kaafi hai. Tanav kamaal ka hai.",
  ],
  boundaryDrought: [
    "Kaafi der se koi boundary nahi. Dabaav chupchap badh raha hai.",
    "Rope nahi mili kaafi over se. Field apna kaam kar rahi hai.",
    "Gendbaazon ne length pakad li, aur run ruk gaye.",
    "Batsman jhunjhla rahe hain, aur tabhi wicket girte hain.",
  ],
  boundaryFlurry: [
    "Boundary par boundary! Momentum badal gaya hai.",
    "Yeh toh poora hamla ban gaya. Gendbaazon ko jawab dhoondhna hoga.",
    "Khel chand gendon mein khul gaya.",
    "Gendbaaz length nahi pakad pa raha, aur keemat chuka raha hai.",
  ],
  partnership50: [
    "Pachas ki saajhedari! Is paari ko sambhal liya inhone.",
    "Pachas run ki partnership, aur dono ab jame hue dikh rahe hain.",
    "Saajhedari ke pachas! Dono ne barabar yogdan diya.",
  ],
  partnershipBuilding: [
    "Achhi saajhedari ban rahi hai, paari sambhal gayi hai.",
    "Dono ache se daud rahe hain aur gap dhoondh rahe hain.",
    "Mushkil daur nikal gaya, ab dono aaram se dikh rahe hain.",
    "Samajhdari se khel rahe hain, aur board chal raha hai.",
  ],
  wicketsInHand: [
    "Bahut wicket haath mein hain, khul kar khel sakte hain.",
    "Ek wicket ka nuksaan uthaa sakte hain, aur yeh khel badal dega.",
  ],
  tailEnd: [
    "Lower order aa gaya, paari jaldi simat sakti hai.",
    "Mannay hue batsman ja chuke hain. Ab har gend khatra hai.",
    "Ab batting patli hai. Ek aur gaya toh paari simat jayegi.",
  ],
  collapse: [
    "Wicket par wicket gir rahe hain. Koi saajhedari nahi ban rahi.",
    "Yeh toh line lag gayi. Koi jam hi nahi paya.",
    "Teen wicket jaldi jaldi, aur paari bikhar rahi hai.",
  ],
  newBatterSettling: [
    "Naya batsman abhi settle ho raha hai.",
    "Crease par naya hai, gendbaaz shuru mein hi test karenge.",
    "Mushkil waqt par aana pada hai, gendbaaz haavi hai.",
  ],
  bowlerOnTop: [
    "Gendbaaz haavi hai, batsman ke paas koi jawab nahi.",
    "Shandaar spell raha hai, wicket ka haqdaar hai.",
  ],
  hatTrickBall: [
    "Do gend mein do wicket! Aur ab hat-trick ki gend. Field simat gayi hai.",
  ],
  goodOverStart: [
    "Naya gendbaaz aaya hai, kaptaan wicket chahta hai.",
    "Naya gendbaaz, naya angle, aur khel badalne ka mauka.",
  ],
};

const BANKS: Record<Lang, Record<string, string[]>> = { en: EN, hi: HI };
const lastPick: Record<string, number> = {};

function pick(lang: Lang, key: string): string | null {
  const bank = BANKS[lang][key];
  if (!bank?.length) return null;
  const memo = `${lang}:${key}`;
  let i = Math.floor(Math.random() * bank.length);
  if (bank.length > 1 && i === lastPick[memo]) i = (i + 1) % bank.length;
  lastPick[memo] = i;
  return bank[i] ?? null;
}

/* ── deciding what is worth saying ──────────────────────────────────────── */

/**
 * Choose a line for the current state, or nothing.
 *
 * Ordered by urgency: a hat-trick ball beats a note about the field being up.
 * Returns null far more often than not, which is the point — the value is in
 * arriving when there is something to say.
 */
export function situational(s: Situation, lang: Lang = "en"): string | null {
  const key = situationKey(s);
  return key ? pick(lang, key) : null;
}

/**
 * Which situation applies, or none.
 *
 * Ordered by urgency: a hat-trick ball beats a note about the field being up.
 * Returns null far more often than not, which is the point — the value is in
 * arriving when there is something to say.
 */
export function situationKey(s: Situation): string | null {
  const isLastOver = s.over === s.totalOvers - 1;
  const wktsLeft = s.maxWickets - s.wickets;

  // Always worth saying.
  // Two in two — the next ball is a hat-trick ball.
  if (s.sinceWicket === 0 && s.wickets >= 2) return "hatTrickBall";
  if (isLastOver && s.over !== 0) return "lastOver";

  // The chase, which is the most interesting thing on the field when it exists.
  if (s.need !== null && s.required !== null && s.ballsLeft !== null && s.ballsLeft > 0) {
    if (s.need <= 6) return "chaseAlmost";
    if (s.required > s.runRate + 4) return "chaseSlipping";
    if (s.required < s.runRate - 2) return "chaseComfortable";
    if (Math.abs(s.required - s.runRate) <= 1.5) return "chaseTight";
  }

  // The shape of the innings.
  if (s.wickets >= 3 && s.sinceWicket < 12 && s.partnership < 15) {
    return "collapse";
  }
  if (wktsLeft <= 2 && s.maxWickets > 3) return "tailEnd";
  if (s.partnership >= 50 && s.partnership < 60) return "partnership50";
  if (s.partnership >= 25 && s.partnershipBalls >= 18) return "partnershipBuilding";

  // Momentum.
  if (s.sinceBoundary >= 18) return "boundaryDrought";
  if (s.sinceBoundary <= 2 && s.runRate > 10) return "boundaryFlurry";
  if (s.dots >= 4) return "bowlerOnTop";
  if (s.sinceWicket <= 3 && s.sinceWicket > 0) return "newBatterSettling";

  // Otherwise, where we are in the innings.
  if (s.phase === "death") return "death";
  if (s.phase === "opening") return "opening";
  if (wktsLeft >= s.maxWickets - 1 && s.over >= 2) return "wicketsInHand";
  return "middle";
}

/** Total lines, for a test to assert against. */
export function count(lang: Lang = "en"): number {
  return Object.values(BANKS[lang]).reduce((a, v) => a + v.length, 0);
}
